/* ── AdminDashboard.jsx ──────────────────────────────────────
   Lists all review periods with submission status.
   Admin clicks a period to open CompanyOverview.
   ─────────────────────────────────────────────────────────── */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, ChevronRight } from 'lucide-react';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import AdminTabs from '../components/Shared/AdminTabs';

export function AdminDashboard() {
  const qc       = useNavigate();
  const queryClient = useQueryClient();
  const [showNew, setShowNew] = useState(false);
  const [newYear,  setNewYear]  = useState(new Date().getFullYear());
  const [newMonth, setNewMonth] = useState(new Date().getMonth() + 1);

  const { data: periods = [], isLoading } = useQuery({
    queryKey: ['periods'],
    queryFn:  () => api.get('/periods'),
  });

  const createMutation = useMutation({
    mutationFn: () => api.post('/periods', { period_year: newYear, period_month: newMonth }),
    onSuccess: (p) => {
      toast.success(`${p.label} created.`);
      queryClient.invalidateQueries(['periods']);
      setShowNew(false);
    },
    onError: (err) => toast.error(err.message),
  });

  const months = ['January','February','March','April','May','June',
                  'July','August','September','October','November','December'];

  if (isLoading) return <LoadingSpinner />;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <AdminTabs />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: '2px', textTransform: 'uppercase', color: '#9CA3AF', marginBottom: 4 }}>Admin</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827' }}>Review Periods</h1>
        </div>
        <button
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 8, background: '#1B2A4A', color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}
          onClick={() => setShowNew(!showNew)}
        >
          <Plus size={15} /> New Period
        </button>
      </div>

      {/* New period form */}
      {showNew && (
        <div style={{ background: '#EFF6FF', border: '1px solid #93C5FD', borderRadius: 10, padding: '16px 20px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#1D4ED8', display: 'block', marginBottom: 4 }}>Year</label>
            <input type="number" style={{ padding: '7px 10px', border: '1px solid #93C5FD', borderRadius: 6, fontSize: 13, width: 90, fontFamily: 'inherit' }} value={newYear} onChange={(e) => setNewYear(+e.target.value)} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#1D4ED8', display: 'block', marginBottom: 4 }}>Month</label>
            <select style={{ padding: '7px 10px', border: '1px solid #93C5FD', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' }} value={newMonth} onChange={(e) => setNewMonth(+e.target.value)}>
              {months.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <button
            style={{ padding: '8px 16px', fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 6, background: '#1D4ED8', color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
      )}

      {/* Periods list */}
      <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
              {['Period', 'Submitted', 'Total CCs', 'Actions'].map((h) => (
                <th key={h} style={{ textAlign: 'left', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#9CA3AF', padding: '10px 16px' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {periods.map((p) => (
              <tr key={p.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                <td style={{ padding: '12px 16px', fontWeight: 600, color: '#111827' }}>{p.label}</td>
                <td style={{ padding: '12px 16px', color: '#374151' }}>{p.submitted_count} / {p.total_count}</td>
                <td style={{ padding: '12px 16px', color: '#374151' }}>{p.total_count}</td>
                <td style={{ padding: '12px 16px' }}>
                  <Link to={`/admin/company/${p.id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#1D4ED8', textDecoration: 'none', fontWeight: 500 }}>
                    View Dashboard <ChevronRight size={13} />
                  </Link>
                </td>
              </tr>
            ))}
            {!periods.length && (
              <tr><td colSpan={4} style={{ textAlign: 'center', padding: 32, color: '#9CA3AF' }}>No periods yet. Create one above.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminDashboard;
